// frontend/app/components/lessonPlans/lessonAuthorFilterRules.js
//
// Narrows the lessons-index list by who wrote it, and lists who else has
// written on this board. No React, no network — like lessonMatch.js, this is
// the piece worth unit-testing on its own; `LessonAuthorFilter` and
// `LessonPlanLauncher` are the only callers.
//
// NAMED "Rules" AND NOT "lessonAuthorFilter.js". That would be one character
// away from `LessonAuthorFilter.js` — same name, differing only in the first
// letter's case — which two files cannot both be checked out at once on a
// case-insensitive filesystem (Windows, and macOS by default): the second
// write silently overwrites the first. `lessonMatch.js` sits next to
// `LessonFinder.js` and `lessonBrowseGroups.js` next to `LessonBrowseTree.js`
// for the same reason — every pure module beside a component it feeds takes a
// name that cannot collide with the component's, not merely a different case
// of it.

/**
 * `filter = { mode: "all" | "mine" | "author", authorId?: string }`
 *
 * `authorId` is an opaque string token (a hash of the owner's email — the
 * student table has no numeric id at all), only read for `mode: "author"`;
 * an unset one falls back to every lesson rather than none, so a filter
 * half-restored from storage (mode carried, id lost) does not silently hide
 * the whole board.
 *
 * Never mutates `lessons`.
 */
export const filterLessonsByAuthor = (lessons, filter) => {
  const list = Array.isArray(lessons) ? lessons : [];
  const mode = filter?.mode || "all";

  switch (mode) {
    case "mine":
      return list.filter((lesson) => lesson?.is_mine === true);
    case "author": {
      const authorId = filter?.authorId;
      if (authorId === null || authorId === undefined) return list;
      return list.filter((lesson) => lesson?.author_id === authorId);
    }
    case "all":
    default:
      return list;
  }
};

/**
 * `[{ author_id, author_name, count }]`, name-sorted, for teacher-authored
 * lessons that are not the viewer's own — the "By teacher" menu exists to
 * find a colleague, so it has nothing to say about a lesson the viewer wrote
 * or one the seeded library shipped with no owner at all.
 *
 * Never mutates `lessons`.
 */
export const authorOptions = (lessons) => {
  const list = Array.isArray(lessons) ? lessons : [];
  const byId = new Map();

  list.forEach((lesson) => {
    if (lesson?.source !== "teacher" || lesson?.is_mine) return;
    const authorId = lesson?.author_id;
    const authorName = lesson?.author_name;
    if (authorId === null || authorId === undefined || !authorName) return;

    const existing = byId.get(authorId);
    if (existing) {
      existing.count += 1;
    } else {
      byId.set(authorId, { author_id: authorId, author_name: authorName, count: 1 });
    }
  });

  return [...byId.values()].sort((a, b) => a.author_name.localeCompare(b.author_name));
};
